// src/app/components/analytics/series/variable-lens.tsx
//
// The Variables lens, and ONLY the Variables lens. One card, two views toggled
// top-right:
//   • Chart (default): all attributes as trend lines on the shared time-series
//     chart (range/projection/events, legend toggles each attribute).
//   • Ranked: a scannable list — label, understanding bar, per-row sparkline,
//     signed delta — sorted by score (or biggest movers). Click a row to drill
//     into that single attribute's trend chart.
// Same data, same order, no backend change. Every other lens is untouched.

import { useMemo, useState } from "react";
import { TrendingUp, TrendingDown, ArrowRight } from "lucide-react";
import { AnalyticsChart } from "../analytics-chart";
import { MetricTerm } from "../../metric-term";
import {
  buildVariableRows,
  buildMemberChartData,
  buildLensChartData,
  type VariableRow,
} from "@/app/services/performance-series/selectors";
import { getMetricDefinition } from "@/app/services/performance-series/metric-definitions";
import { chartSuffixFor, type Lens } from "@/app/services/performance-series/registry";
import type { NormalizedEnvelope, DimensionName } from "@/app/services/performance-series/types";
import type { PerformanceEvent } from "@/app/data/performance-events";

// Direction colors (literal hex — SVG can't resolve CSS var()). Mapped to tokens:
// teal #0E9C93 (~--alpha-teal, legible on white), coral #FF6D66 (--infrared-coral),
// grey #9AA0A6 (~--muted-foreground).
const UP = "#0E9C93";
const DOWN = "#FF6D66";
const FLAT = "#9AA0A6";
// A null delta (single reading) is neither up nor down: it is not yet a trend.
const dirColor = (delta: number | null) => (delta == null ? FLAT : delta > 0 ? UP : delta < 0 ? DOWN : FLAT);

function Sparkline({ values, color, width = 84, height = 30 }: { values: number[]; color: string; width?: number; height?: number }) {
  const pad = 3;
  if (values.length === 0) return <svg width={width} height={height} />;
  if (values.length === 1) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <circle cx={width / 2} cy={height / 2} r={2.5} fill={color} />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const n = values.length;
  const pts = values.map((v, i) => {
    const x = (i / (n - 1)) * (width - pad * 2) + pad;
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y] as const;
  });
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const [px, py] = pts[i - 1];
    const [cx, cy] = pts[i];
    const mx = (px + cx) / 2;
    const my = (py + cy) / 2;
    d += ` Q ${px.toFixed(1)} ${py.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
    if (i === pts.length - 1) d += ` L ${cx.toFixed(1)} ${cy.toFixed(1)}`;
  }
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: "visible" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DeltaTag({ delta, unit }: { delta: number | null; unit: string }) {
  // One reading has no baseline to difference against. Rendering "0.0" there
  // claims the metric was measured twice and did not move, which is false.
  if (delta == null) {
    return (
      <span
        className="inline-flex items-center"
        style={{ gap: 4, fontSize: 12.5, fontWeight: 500, color: FLAT, whiteSpace: "nowrap" }}
        title="Only one measurement run so far, so there is nothing to compare against yet."
      >
        first reading
      </span>
    );
  }
  const color = dirColor(delta);
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : ArrowRight;
  const text = delta === 0 ? `0${unit}` : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}${unit}`;
  return (
    <span className="inline-flex items-center" style={{ gap: 4, fontSize: 13, fontWeight: 600, color, fontVariantNumeric: "tabular-nums" }}>
      <Icon style={{ width: 14, height: 14 }} />
      {text}
    </span>
  );
}

function segBtn(on: boolean): React.CSSProperties {
  return {
    border: "none", cursor: "pointer", borderRadius: 7, padding: "5px 12px", fontSize: 13,
    fontWeight: on ? 600 : 500,
    background: on ? "var(--card)" : "transparent",
    color: on ? "var(--foreground)" : "var(--muted-foreground)",
    boxShadow: on ? "0 1px 2px rgba(13,13,15,.12)" : "none",
  };
}

interface Props {
  envelope: NormalizedEnvelope;
  lens: Lens;
  brandName?: string;
  projectionDate?: string | null;
  events?: PerformanceEvent[];
  onNavigateToInsights?: () => void;
}

export function VariableLens({ envelope, lens, brandName, projectionDate, events, onNavigateToInsights }: Props) {
  const dimension: DimensionName = lens.dimensions[0] ?? "variable";
  const [view, setView] = useState<"chart" | "ranked">("chart"); // default: the trend chart
  const [openKey, setOpenKey] = useState<string | null>(null);

  const rows = useMemo(() => buildVariableRows(envelope, lens.layer, dimension), [envelope, lens.layer, dimension]);
  const trend = useMemo(() => buildLensChartData(envelope, lens.layer, dimension), [envelope, lens.layer, dimension]);
  const unit = chartSuffixFor(lens.unit);

  const openRow = openKey ? rows.find((r) => r.key === openKey) ?? null : null;
  const drill = useMemo(
    () => (openRow ? buildMemberChartData(envelope, lens.layer, dimension, openRow.key) : null),
    [openRow, envelope, lens.layer, dimension],
  );

  const empty = rows.length === 0;

  const def = getMetricDefinition(lens.key);
  const titleNode = def
    ? <MetricTerm definition={def.definition} calculation={def.calculation} interpretation={def.interpretation} goodRange={def.goodRange}>{lens.label}</MetricTerm>
    : lens.label;
  const heading = (
    <h3 style={{ fontSize: "clamp(var(--text-lg), 3.5vw, var(--text-xl))", fontWeight: "var(--font-weight-semibold)", marginBottom: 2 }}>
      {titleNode}
    </h3>
  );
  const viewToggle = (
    <div role="tablist" aria-label="Attribute view" style={{ display: "inline-flex", gap: 3, padding: 3, background: "var(--muted)", borderRadius: 9 }}>
      {([["chart", "Chart"], ["ranked", "Ranked"]] as const).map(([val, label]) => (
        <button key={val} role="tab" aria-selected={view === val} onClick={() => setView(val)} style={segBtn(view === val)}>
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      {/* Chart view: the title + Chart/Ranked toggle render INSIDE the chart
          card's headline slot (headerContent below), where other charts show
          their title and value. Ranked view shows the same header above the
          list. */}
      {(empty || view === "ranked") && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          {heading}
          {!empty && viewToggle}
        </div>
      )}

      {empty ? (
        <div style={{ padding: "20px 0", textAlign: "center", color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
          No attribute data for this brand yet.
        </div>
      ) : view === "chart" ? (
        // Trend chart: all attributes as lines. Its own headline is hidden (a
        // single aggregate % across unrelated attributes is meaningless); the
        // persistent header above carries the title + toggle.
        <AnalyticsChart
          timeSeriesData={trend.timeSeriesData}
          selectedModels={trend.seriesKeys}
          isLoading={false}
          error={null}
          metricUnit={trend.metricUnit}
          metricName={lens.label}
          metricTitle={lens.label}
          headerContent={
            <div className="flex flex-col" style={{ gap: 6 }}>
              {heading}
              {viewToggle}
            </div>
          }
          brandName={brandName}
          onNavigateToInsights={onNavigateToInsights}
          projectionDate={projectionDate}
          events={events}
        />
      ) : (
        <div className="flex flex-col" style={{ gap: 12 }}>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden" }}>
            {rows.map((row: VariableRow, i) => {
              const open = row.key === openKey;
              const color = dirColor(row.delta);
              return (
                <div key={row.key} style={{ borderTop: i === 0 ? "none" : "1px solid var(--border)" }}>
                  <button
                    onClick={() => setOpenKey(open ? null : row.key)}
                    aria-expanded={open}
                    className="w-full"
                    style={{
                      display: "grid",
                      // Fixed columns so every row's bar starts at the SAME left edge.
                      gridTemplateColumns: "200px 1fr 172px",
                      alignItems: "center",
                      gap: 16,
                      padding: "14px 16px",
                      background: open ? "var(--muted-30, var(--muted))" : "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 500, color: "var(--foreground)", lineHeight: 1.3, overflowWrap: "anywhere" }}>
                      {row.label}
                    </span>
                    <div className="flex items-center" style={{ gap: 12, minWidth: 0 }}>
                      <div style={{ flex: 1, height: 10, borderRadius: 9999, background: "var(--muted)", overflow: "hidden", minWidth: 60 }}>
                        <div style={{ height: 10, width: `${Math.max(0, Math.min(100, row.score))}%`, borderRadius: 9999, background: "var(--alpha-teal)" }} />
                      </div>
                      <span style={{ fontSize: 18, fontWeight: 700, color: "#0E9C93", fontVariantNumeric: "tabular-nums", minWidth: 34, textAlign: "right" }}>
                        {row.score.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex items-center" style={{ gap: 14, justifyContent: "flex-end" }}>
                      <Sparkline values={row.history} color={color} />
                      <DeltaTag delta={row.delta} unit={unit} />
                    </div>
                  </button>

                  {open && drill && (
                    <div style={{ padding: "4px 16px 16px" }}>
                      <AnalyticsChart
                        timeSeriesData={drill.timeSeriesData}
                        selectedModels={drill.seriesKeys}
                        isLoading={false}
                        error={null}
                        metricUnit={drill.metricUnit}
                        metricName={row.label}
                        metricTitle={row.label}
                        brandName={brandName}
                        onNavigateToInsights={onNavigateToInsights}
                        projectionDate={projectionDate}
                        events={events}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center" style={{ gap: 20, fontSize: 12, color: "var(--muted-foreground)" }}>
            <span className="inline-flex items-center" style={{ gap: 7 }}>
              <span style={{ width: 24, height: 8, borderRadius: 9999, background: "var(--alpha-teal)" }} />
              Understanding score
            </span>
            <span className="inline-flex items-center" style={{ gap: 7 }}>
              <Sparkline values={[2, 5, 4, 8]} color={FLAT} width={28} height={14} />
              Trend
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
